/* "Correct a logged refuel" — the office's answer to a flagged contradiction.
 *
 * Only ever rendered from an alert that says these fuel figures disagree with
 * something (see `alertInvitesRefuelCorrection`). That siting is the design, not
 * a convenience: a permanent edit control on every refuel row invites tidying
 * numbers until they look right, which from the outside is indistinguishable
 * from covering a loss. Opening it from a flag means every correction has a
 * reason attached to a question somebody asked first.
 *
 * The driver's original figures are never overwritten — see `refuelCorrection`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  correctRefuelEvent,
  originalOf,
  RefuelCorrectionError,
  type RefuelCollection,
} from '../../services/refuelCorrection';

interface RefuelRow {
  id: string;
  at: Date;
  liters: number | null;
  cost: number | null;
  voided: boolean;
  corrected: boolean;
  correctionReason?: string;
  original: ReturnType<typeof originalOf>;
}

export interface CorrectRefuelPanelProps {
  busId: string;
  busLabel: string;
  collectionName?: RefuelCollection;
  /** Called after a successful correction so the caller can refresh/toast. */
  onCorrected?: (eventId: string) => void;
}

const money = (n: number | null) => (n == null ? '—' : `KES ${Math.round(n).toLocaleString()}`);
const litres = (n: number | null) => (n == null ? '—' : `${n} L`);

export default function CorrectRefuelPanel({
  busId,
  busLabel,
  collectionName = 'busRefuelEvents',
  onCorrected,
}: CorrectRefuelPanelProps) {
  const [rows, setRows] = useState<RefuelRow[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [litersInput, setLitersInput] = useState('');
  const [costInput, setCostInput] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!busId) {
      setRows([]);
      return;
    }
    /* No orderBy: `where` + `orderBy` needs a composite index that may not
       exist, and a missing index fails the query outright rather than degrading
       — the same trap documented on the refuel listener in BusTracking. Sorted
       on the client instead.

       BUT `limit` IS APPLIED BEFORE THAT SORT, which made the cap a correctness
       bug rather than a budget one: at 25 the query returned an arbitrary 25
       documents by document id, and the client then sorted *those*. A bus with
       more than 25 fills could therefore have its most recent — the one being
       corrected — silently outside the slice, so the odometer line the owner
       came here to fix would simply not be listed.

       Raised rather than ordered. `busRefuelEvents (busId ASC, timestamp DESC)`
       IS declared in pear_mobile/firestore.indexes.json, so an orderBy would
       probably work — but "probably" fails this query to empty, and a one-shot
       read of a few hundred small documents costs nothing next to that risk. */
    getDocs(query(collection(db, collectionName), where('busId', '==', busId), limit(300)))
      .then((snap) => {
        if (cancelled) return;
        const list: RefuelRow[] = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const ts = data.timestamp as Timestamp | undefined;
          const created = data.createdAt as Timestamp | undefined;
          return {
            id: d.id,
            at: ts?.toDate?.() ?? created?.toDate?.() ?? new Date(0),
            liters: typeof data.litersAdded === 'number' ? data.litersAdded : null,
            cost: typeof data.costEstimate === 'number' ? data.costEstimate : null,
            voided: data.voided === true,
            corrected: data.corrected === true,
            correctionReason:
              typeof data.correctionReason === 'string' ? data.correctionReason : undefined,
            original: originalOf(data),
          };
        });
        list.sort((a, b) => b.at.getTime() - a.at.getTime());
        setRows(list);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [busId, collectionName, reloadKey]);

  const correctable = useMemo(() => (rows ?? []).filter((r) => !r.voided).slice(0, 8), [rows]);

  const open = (r: RefuelRow) => {
    setOpenId(r.id);
    setLitersInput(r.liters == null ? '' : String(r.liters));
    setCostInput(r.cost == null ? '' : String(r.cost));
    setReason('');
    setError(null);
    setDone(null);
  };

  const submit = async (r: RefuelRow) => {
    setBusy(true);
    setError(null);
    try {
      const nextL = litersInput.trim() === '' ? undefined : Number(litersInput);
      const nextC = costInput.trim() === '' ? undefined : Number(costInput);
      await correctRefuelEvent(
        r.id,
        {
          // Only send what actually moved, so an untouched field is never
          // rewritten to the same value and stamped as "corrected".
          litersAdded: nextL !== undefined && nextL !== r.liters ? nextL : undefined,
          costEstimate: nextC !== undefined && nextC !== r.cost ? nextC : undefined,
          reason,
        },
        collectionName,
      );
      setDone(r.id);
      setOpenId(null);
      setReloadKey((k) => k + 1);
      onCorrected?.(r.id);
    } catch (e) {
      setError(
        e instanceof RefuelCorrectionError
          ? e.message
          : "That didn't save. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (rows === null) {
    return <p className="fd-tiny fd-muted">Loading {busLabel}'s refuels…</p>;
  }
  if (!correctable.length) {
    return (
      <p className="fd-tiny fd-muted">
        No refuels on file for {busLabel} to correct. If the figures look wrong because nothing
        was logged at all, the fix is a driver logging it, not an edit here.
      </p>
    );
  }

  return (
    <div>
      <p className="fd-tiny fd-muted" style={{ margin: '0 0 10px' }}>
        Correcting keeps what the driver originally reported beside your change, so the record
        still shows both.
      </p>
      {correctable.map((r) => (
        <div
          key={r.id}
          className="fd-card"
          style={{ padding: 12, marginBottom: 8, borderRadius: 10 }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>
              {litres(r.liters)} · {money(r.cost)}
            </span>
            <span className="fd-tiny fd-muted">{r.at.toLocaleDateString()}</span>
            {r.corrected && <span className="fd-pill">Corrected</span>}
            {done === r.id && <span className="fd-pill">Saved</span>}
            <button
              type="button"
              className="fd-btn fd-btn-white fd-btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => (openId === r.id ? setOpenId(null) : open(r))}
            >
              {openId === r.id ? 'Cancel' : 'Correct this'}
            </button>
          </div>

          {r.original && (
            <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
              Driver originally reported {litres(r.original.litersAdded)} ·{' '}
              {money(r.original.costEstimate)}
              {r.correctionReason ? ` — corrected because: ${r.correctionReason}` : ''}
            </p>
          )}

          {openId === r.id && (
            <div
              style={{
                marginTop: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                width: '100%',
                minWidth: 0,
              }}
            >
              <label
                className="fd-tiny"
                style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', minWidth: 0 }}
              >
                Litres
                <input
                  className="fd-input"
                  inputMode="decimal"
                  value={litersInput}
                  onChange={(e) => setLitersInput(e.target.value)}
                  placeholder="Leave as is to keep it"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              <label
                className="fd-tiny"
                style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', minWidth: 0 }}
              >
                Cost (KES)
                <input
                  className="fd-input"
                  inputMode="numeric"
                  value={costInput}
                  onChange={(e) => setCostInput(e.target.value)}
                  placeholder="Leave as is to keep it"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              <label
                className="fd-tiny"
                style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', minWidth: 0 }}
              >
                Why is this being corrected?
                <input
                  className="fd-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. receipt says 40 L, driver typed 4"
                  maxLength={300}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              {error && (
                <p className="fd-tiny" style={{ color: 'var(--fd-crit-600)', margin: 0 }}>
                  {error}
                </p>
              )}
              <div>
                {/* Not fd-btn-onbrand: that style is white-on-transparent for dark
                    hero surfaces. On this white card it made Save invisible. */}
                <button
                  type="button"
                  className="fd-btn fd-btn-primary fd-btn-sm"
                  disabled={busy}
                  onClick={() => void submit(r)}
                >
                  {busy ? 'Saving…' : 'Save correction'}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
